import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthSessionDto, UserDto } from '@fiesta/shared';
import { api } from './api.js';

// Web auth state (§6 screen 9). Sessions are httpOnly cookies set by the API
// (credentials:'include'), so we never hold tokens in JS — we just track the
// current user via /auth/me.
interface AuthState {
  user: UserDto | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    username: string;
    password: string;
    accountType: 'host' | 'participant';
  }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // /auth/me returns { user }, unlike login/register which return { user, accessToken }.
      const res = await api.get<{ user: UserDto }>('/auth/me');
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (identifier: string, password: string) => {
    const res = await api.post<AuthSessionDto>('/auth/login', { identifier, password });
    setUser(res.user);
  }, []);

  const register = useCallback(
    async (input: { email: string; username: string; password: string; accountType: 'host' | 'participant' }) => {
      const res = await api.post<AuthSessionDto>('/auth/register', input);
      setUser(res.user);
    },
    [],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
